import type { LongformChapter } from '../content/longform';
import type { PhotoSpec } from './primitives';
import type { StorySlideSpec } from './stories-viewer';

export interface StoryPhotoAnchor {
  id: string;
  sectionIndex: number;
  photo: PhotoSpec;
}

export interface StorySlideCopy {
  eyebrow?: string;
  title?: string;
  text: string;
  autoAdvance?: boolean;
}

export interface StoryChapterCopy {
  summary?: string;
  slides?: Readonly<Record<string, StorySlideCopy>>;
}

function openingSlide(
  chapter: LongformChapter,
  photo: PhotoSpec,
  summary: string,
): StorySlideSpec {
  return {
    photo,
    eyebrow: `${chapter.index} · ${chapter.era}`,
    title: chapter.title,
    text: summary,
    autoAdvance: false,
  };
}

export function createStorySlides(
  chapter: LongformChapter,
  anchors: readonly StoryPhotoAnchor[],
  coverPhoto: PhotoSpec,
  editorial: StoryChapterCopy = {},
): StorySlideSpec[] {
  const summary = editorial.summary ?? chapter.dek;
  if (anchors.length === 0) return [openingSlide(chapter, coverPhoto, summary)];

  const slides: StorySlideSpec[] = [openingSlide(chapter, coverPhoto, summary)];
  let skippedCoverAnchor = false;
  for (const anchor of anchors) {
    if (!skippedCoverAnchor && anchor.photo.key === coverPhoto.key) {
      skippedCoverAnchor = true;
      continue;
    }
    const copy = editorial.slides?.[anchor.id];
    if (!copy) throw new Error(`Missing editorial story copy for ${anchor.id}`);

    const section = chapter.sections[anchor.sectionIndex];
    if (!section) throw new Error(`Missing story section ${anchor.sectionIndex} for chapter ${chapter.id}`);

    const slide: StorySlideSpec = {
      photo: anchor.photo,
      title: copy.title ?? section.title,
      text: copy.text,
      autoAdvance: copy.autoAdvance ?? true,
    };
    const eyebrow = copy.eyebrow ?? section.eyebrow;
    if (eyebrow) slide.eyebrow = eyebrow;
    slides.push(slide);
  }
  return slides;
}
